import React from 'react';
import { useT } from '../../i18n';

// What the index becomes at the next reconstitution.
//
// This is a forecast, not the live index: the current weights stay frozen until
// the review date. The table therefore shows the CURRENT top 20 by market cap
// with the weights they would receive, and calls out what enters and leaves.

const trillions = (cap) => (cap ? `${(cap / 1e12).toFixed(3)}T` : '—');

export default function TargetWeights({ reconstitution, schedule, growthRows }) {
    const { t, fmt } = useT();

    // The reason arrives from the API in English. It is a diagnostic rather
    // than copy, so it is interpolated as-is rather than mapped to a key.
    if (!reconstitution?.available) {
        return (
            <p className="desc">
                {reconstitution?.unavailableReason
                    ? t('targets.unavailableWhy', { reason: reconstitution.unavailableReason })
                    : t('targets.unavailable')}
            </p>
        );
    }

    const { targets, entering, leaving, asOf } = reconstitution;
    const entrySymbols = new Set(entering.map((e) => e.symbol));

    return (
        <>
            <div className="rebal__summary">
                <p className="rebal__note">
                    {t('targets.lede', {
                        date: fmt.date(schedule?.nextReconstitution) ?? '—',
                        asOf: fmt.date(asOf) ?? '—',
                    })}
                </p>
                {(entering.length > 0 || leaving.length > 0) && (
                    <p className="rebal__alert">
                        {entering.length > 0 && (
                            <>
                                {t('targets.entering', {
                                    symbols: entering.map((e) => `${e.symbol} (#${e.rank})`).join(', '),
                                })}{' '}
                            </>
                        )}
                        {leaving.length > 0 && (
                            <>
                                {t('targets.leaving', {
                                    symbols: leaving
                                        .map((l) => `${l.symbol}${l.liveRank ? ` (#${l.liveRank})` : ''}`)
                                        .join(', '),
                                })}
                            </>
                        )}
                    </p>
                )}
            </div>

            <div className="positions__scroll">
                <table className="positions">
                    <thead>
                        <tr>
                            <th className="is-left">#</th>
                            <th className="is-left is-wide">{t('targets.asset')}</th>
                            <th className="is-left">{t('targets.symbol')}</th>
                            <th className="is-right">{t('targets.marketCap')}</th>
                            <th className="is-right">{t('targets.weight')}</th>
                            <th className="is-left">{t('targets.status')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {targets.map((row) => (
                            <tr
                                key={row.symbol}
                                className={entrySymbols.has(row.symbol) ? 'is-entering' : undefined}
                            >
                                <td className="is-left is-rank">{row.rank}</td>
                                <td className="is-left is-wide">{row.name}</td>
                                <td className="is-left">{row.symbol}</td>
                                <td className="is-right">{trillions(row.marketCap)}</td>
                                <td className="is-right">{fmt.number(row.targetPct)}%</td>
                                <td className="is-left">
                                    {entrySymbols.has(row.symbol) ? (
                                        <span className="tag tag--new">{t('targets.newConstituent')}</span>
                                    ) : row.held ? (
                                        <span className="tag">{t('targets.held')}</span>
                                    ) : (
                                        <span className="tag tag--warn">{t('targets.notHeld')}</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                        <tr className="is-subtotal">
                            <td colSpan="4" className="is-left">
                                {t('targets.assetSleeve')}
                            </td>
                            <td className="is-right">{fmt.number(90)}%</td>
                            <td />
                        </tr>
                    </tbody>
                </table>
            </div>

            <h3 className="alloc__title tw__heading">{t('targets.growthSleeve', { pct: 10 })}</h3>
            <div className="positions__scroll">
                <table className="positions">
                    <thead>
                        <tr>
                            <th className="is-left is-wide">{t('targets.theme')}</th>
                            <th className="is-left">{t('targets.symbol')}</th>
                            <th className="is-right">{t('targets.weight')}</th>
                            <th className="is-right">{t('targets.current')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {growthRows.map((r) => (
                            <tr key={r.symbol}>
                                <td className="is-left is-wide">{r.theme}</td>
                                <td className="is-left">{r.symbol}</td>
                                <td className="is-right">{fmt.number(r.targetPct)}%</td>
                                <td className="is-right">
                                    {r.currentPct === null ? '—' : `${fmt.number(r.currentPct)}%`}
                                </td>
                            </tr>
                        ))}
                        <tr className="is-subtotal">
                            <td colSpan="2" className="is-left">
                                {t('targets.growthSleeveRow')}
                            </td>
                            <td className="is-right">{fmt.number(10)}%</td>
                            <td />
                        </tr>
                    </tbody>
                </table>
            </div>
        </>
    );
}
